import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import { FW } from '../../theme/tokens';

/**
 * Sales Portal visual tokens. These are the same Fab Works tokens the app-wide
 * theme is built from — the portal keeps its identity regardless of theme preset.
 */
export const SALES = {
  navy: FW.navy,
  navyMid: FW.navyMid,
  steel: FW.steel,
  steelSoft: FW.steelSoft,
  ink: FW.ink,
  muted: FW.muted,
  line: FW.line,
  surface: FW.surface,
  white: FW.white,
  success: FW.success,
  successSoft: FW.successSoft,
  warning: FW.warning,
  warningSoft: FW.warningSoft,
  danger: FW.danger,
  dangerSoft: FW.dangerSoft,
  info: FW.info,
  infoSoft: FW.infoSoft,
};

export const LEAD_STATUS_META = {
  new: { label: 'New', color: SALES.info, bg: SALES.infoSoft },
  contacted: { label: 'Contacted', color: SALES.steel, bg: SALES.steelSoft },
  quoting: { label: 'Quoting', color: SALES.warning, bg: SALES.warningSoft },
  converted: { label: 'Converted', color: SALES.success, bg: SALES.successSoft },
  lost: { label: 'Lost', color: SALES.muted, bg: '#EEF2F6' },
  dead: { label: 'Dead', color: SALES.muted, bg: '#EEF2F6' },
};

export const QUOTE_REQUEST_STATUS_META = {
  draft: { label: 'Draft', color: SALES.muted, bg: '#EEF2F6' },
  submitted: { label: 'Submitted', color: SALES.info, bg: SALES.infoSoft },
  in_review: { label: 'In Review', color: SALES.warning, bg: SALES.warningSoft },
  approved: { label: 'Approved', color: SALES.success, bg: SALES.successSoft },
  rejected: { label: 'Rejected', color: SALES.danger, bg: SALES.dangerSoft },
  withdrawn: { label: 'Withdrawn', color: SALES.muted, bg: '#EEF2F6' },
};

export const EXPENSE_STATUS_META = {
  pending: { label: 'Pending', color: SALES.warning, bg: SALES.warningSoft },
  approved: { label: 'Approved', color: SALES.success, bg: SALES.successSoft },
  rejected: { label: 'Rejected', color: SALES.danger, bg: SALES.dangerSoft },
  paid: { label: 'Paid', color: SALES.info, bg: SALES.infoSoft },
};

export const ACTIVITY_TYPE_META = {
  phone: { label: 'Phone', color: SALES.steel, bg: SALES.steelSoft },
  onsite_visit: { label: 'Onsite Visit', color: SALES.warning, bg: SALES.warningSoft },
  email: { label: 'Email', color: SALES.info, bg: SALES.infoSoft },
  other: { label: 'Other', color: SALES.muted, bg: '#EEF2F6' },
};

export function formatStatusLabel(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function companyInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export function SalesStatusChip({ status, metaMap = LEAD_STATUS_META }) {
  const meta = metaMap[status] || {
    label: formatStatusLabel(status),
    color: SALES.muted,
    bg: '#EEF2F6',
  };
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: '0.72rem',
        letterSpacing: 0.2,
        color: meta.color,
        bgcolor: meta.bg,
        border: `1px solid ${meta.color}22`,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

export function SalesPageHeader({
  title,
  subtitle,
  actions,
  eyebrow = 'Sales Portal',
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 2.5,
      }}
    >
      <Box>
        <Typography
          variant="overline"
          sx={{ color: SALES.steel, fontWeight: 700, letterSpacing: 1.2, lineHeight: 1 }}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mt: 0.5,
            fontSize: { xs: '1.45rem', sm: '1.7rem' },
            fontWeight: 700,
            color: SALES.ink,
            letterSpacing: -0.3,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ mt: 0.5, color: SALES.muted, maxWidth: 620 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      ) : null}
    </Box>
  );
}

export function SalesStatCard({ label, value, hint, accent = SALES.steel }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${SALES.line}`,
        bgcolor: SALES.white,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: accent,
        },
      }}
    >
      <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, color: SALES.ink, fontSize: '1.75rem' }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" sx={{ color: SALES.muted }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

export function SalesEmptyState({
  title,
  description,
  actionLabel,
  onAction,
  icon = null,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        py: 6,
        px: 3,
        textAlign: 'center',
        borderRadius: 2,
        border: `1px dashed ${SALES.line}`,
        bgcolor: SALES.white,
      }}
    >
      {icon ? (
        <Box sx={{ mb: 1.5, color: SALES.steel, display: 'flex', justifyContent: 'center' }}>
          {icon}
        </Box>
      ) : null}
      <Typography variant="h6" sx={{ fontWeight: 700, color: SALES.ink }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" sx={{ mt: 0.75, color: SALES.muted, maxWidth: 420, mx: 'auto' }}>
          {description}
        </Typography>
      ) : null}
      {actionLabel && onAction ? (
        <Button variant="contained" onClick={onAction} sx={{ mt: 2.5 }}>
          {actionLabel}
        </Button>
      ) : null}
    </Paper>
  );
}

export function SalesEntityAvatar({ name, size = 36 }) {
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size > 32 ? '0.85rem' : '0.75rem',
        fontWeight: 700,
        bgcolor: SALES.steelSoft,
        color: SALES.steel,
        border: `1px solid ${SALES.steel}33`,
      }}
    >
      {companyInitials(name)}
    </Avatar>
  );
}

export function SalesCard({ children, sx = {}, ...rest }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${SALES.line}`,
        bgcolor: SALES.white,
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  );
}

export function SalesSectionCard({ title, subtitle, action, children, sx = {} }) {
  return (
    <SalesCard sx={{ mb: 2, ...sx }}>
      {(title || action) && (
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${SALES.line}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#FAFBFC',
          }}
        >
          <Box>
            {title ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: SALES.ink }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="caption" sx={{ color: SALES.muted }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </SalesCard>
  );
}

export const salesPrimaryButtonSx = {
  bgcolor: SALES.steel,
  boxShadow: 'none',
  fontWeight: 600,
  textTransform: 'none',
  px: 2,
  '&:hover': { bgcolor: '#155a8f', boxShadow: 'none' },
};

export const salesTableHeadSx = {
  bgcolor: '#F7F9FB',
  '& .MuiTableCell-head': {
    color: SALES.muted,
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: `1px solid ${SALES.line}`,
    py: 1.25,
  },
};

export const salesRowHoverSx = {
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': { bgcolor: SALES.steelSoft },
  '& .MuiTableCell-root': {
    borderBottomColor: SALES.line,
    py: 1.5,
  },
};
